import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Flotante, display } from '../asistencia/ui';

// Lo propio de Puertas sobre el kit de Asistencia (mismos paneles, botones y letras de
// Centro SURA): el botón que se mantiene presionado, el interruptor y las ventanas.

const MANTENER_MS = 900;

/**
 * Botón que se confirma manteniéndolo presionado: una acción sobre el edificio no se
 * dispara por un toque sin querer. Con teclado, mantener Enter o Espacio.
 */
export const MantenerParaConfirmar = ({ children, onConfirmar, disabled, ocupado, principal = true, peligro = false, className = '' }) => {
    const relleno = useRef(null);
    const inicio = useRef(null);
    const raf = useRef(0);
    const [hecho, setHecho] = useState(false);

    const pintar = (fraccion, conTransicion) => {
        const el = relleno.current;
        if (!el) return;
        el.style.transition = conTransicion ? 'transform 0.25s ease-out' : 'none';
        el.style.transform = `scaleX(${fraccion})`;
    };

    const soltar = () => {
        if (inicio.current == null) return;
        cancelAnimationFrame(raf.current);
        inicio.current = null;
        pintar(0, true);
    };

    const presionar = () => {
        if (disabled || ocupado || inicio.current != null) return;
        inicio.current = performance.now();
        const reducido = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const paso = (ahora) => {
            const f = Math.min(1, (ahora - inicio.current) / MANTENER_MS);
            if (!reducido) pintar(f, false);
            if (f >= 1) {
                inicio.current = null;
                setHecho(true);
                onConfirmar();
                setTimeout(() => { setHecho(false); pintar(0, true); }, 1300);
                return;
            }
            raf.current = requestAnimationFrame(paso);
        };
        raf.current = requestAnimationFrame(paso);
    };

    useEffect(() => () => cancelAnimationFrame(raf.current), []);

    const colores = peligro
        ? 'border-rose-200 text-rose-700 bg-white hover:bg-rose-50'
        : principal
            ? 'border-sura-blue bg-sura-blue text-white hover:bg-sura-blue-dark'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
    return (
        <button
            type="button" disabled={disabled || ocupado}
            data-relleno={peligro ? 'peligro' : principal ? 'claro' : 'azul'}
            onPointerDown={presionar} onPointerUp={soltar} onPointerLeave={soltar} onPointerCancel={soltar}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) { e.preventDefault(); presionar(); } }}
            onKeyUp={(e) => { if (e.key === 'Enter' || e.key === ' ') soltar(); }}
            onContextMenu={(e) => e.preventDefault()}
            className={`puerta-mantener h-10 px-4 rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-2 select-none touch-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40 disabled:cursor-not-allowed ${colores} ${className}`}
        >
            <span className="puerta-mantener-relleno" ref={relleno} aria-hidden />
            {hecho ? <Check size={16} /> : null}
            <span>{ocupado ? 'Enviando' : children}</span>
        </button>
    );
};

export const Interruptor = ({ activo, onCambio, etiqueta, disabled }) => (
    <button
        type="button" role="switch" aria-checked={activo} aria-label={etiqueta} disabled={disabled}
        onClick={() => onCambio(!activo)}
        className={`relative w-10 h-6 rounded-full transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40 ${
            activo ? 'bg-sura-blue' : 'bg-slate-200'
        }`}
    >
        <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${activo ? 'translate-x-4' : ''}`} />
    </button>
);

// Encabezado de cada vista: título y una línea que dice para qué sirve
export const Encabezado = ({ titulo, children, acciones }) => (
    <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
            <h1 className={`${display} text-3xl font-bold text-slate-900`}>{titulo}</h1>
            {children && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{children}</p>}
        </div>
        {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
    </header>
);

export const Vacio = ({ icono: Icono, titulo, children }) => (
    <div className="p-12 text-center">
        {Icono && <Icono size={36} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />}
        <p className="text-slate-600 font-medium">{titulo}</p>
        {children && <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">{children}</p>}
    </div>
);

// Ventana sobre todo (confirmaciones, altas): se cierra con Escape o tocando afuera
export const Ventana = ({ titulo, onCerrar, children, ancho = 'max-w-md', bloqueada = false }) => {
    useEffect(() => {
        const tecla = (e) => e.key === 'Escape' && !bloqueada && onCerrar();
        window.addEventListener('keydown', tecla);
        return () => window.removeEventListener('keydown', tecla);
    }, [bloqueada, onCerrar]);
    return (
        <Flotante>
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-slate-900/40" onClick={() => !bloqueada && onCerrar()} />
                <div role="dialog" aria-modal="true" aria-label={titulo} className={`relative w-full ${ancho} bg-white rounded-2xl shadow-xl p-6 puertas-aparece`}>
                    <h2 className={`${display} text-lg font-bold text-slate-900`}>{titulo}</h2>
                    {children}
                </div>
            </div>
        </Flotante>
    );
};
